/**
 * Auth Context for Platform Admin
 * Only platform admins can access this dashboard
 */

import React, { createContext, useContext, useState, useEffect } from 'react';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://api.mango-business.solutions/api/v1';

interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  isPlatformAdmin: boolean;
}

interface AuthContextType {
  isAuthenticated: boolean;
  user: User | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<{ success: boolean; error?: string }>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check for existing session
    const token = localStorage.getItem('platformAdminToken');
    if (token) {
      verifyToken(token);
    } else {
      setLoading(false);
    }
  }, []);

  const verifyToken = async (token: string) => {
    try {
      const response = await fetch(`${API_BASE_URL}/auth/me`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        // Check if user is platform admin
        if (data.staff?.permissions?.platformAdmin) {
          setUser({
            id: data.staff.id,
            email: data.staff.email,
            firstName: data.staff.firstName,
            lastName: data.staff.lastName,
            isPlatformAdmin: true,
          });
          setIsAuthenticated(true);
        } else {
          localStorage.removeItem('platformAdminToken');
        }
      } else {
        localStorage.removeItem('platformAdminToken');
      }
    } catch (error) {
      console.error('Token verification failed:', error);
      localStorage.removeItem('platformAdminToken');
    } finally {
      setLoading(false);
    }
  };

  const login = async (email: string, password: string) => {
    try {
      const response = await fetch(`${API_BASE_URL}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password })
      });

      const data = await response.json();
      
      // Backend returns tokens in different formats:
      // Staff: { tokens: { staff: "...", personal?: "..." }, user: {...} }
      // Business: { token: "...", user: {...} }
      let token: string | undefined;
      if (data.tokens) {
        token = data.tokens.staff || data.tokens.personal || data.tokens.business;
      } else if (data.token) {
        token = data.token;
      }

      if (token) {
        // Verify this user is a platform admin
        const meResponse = await fetch(`${API_BASE_URL}/auth/me`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const meData = await meResponse.json();

        if (meData.staff?.permissions?.platformAdmin) {
          localStorage.setItem('platformAdminToken', token);
          setUser({
            id: meData.staff.id,
            email: meData.staff.email,
            firstName: meData.staff.firstName,
            lastName: meData.staff.lastName,
            isPlatformAdmin: true,
          });
          setIsAuthenticated(true);
          return { success: true };
        } else {
          return { success: false, error: 'You do not have platform admin access' };
        }
      }

      return { success: false, error: data.error || 'Login failed' };
    } catch (error) {
      console.error('Login error:', error);
      return { success: false, error: 'Network error' };
    }
  };

  const logout = () => {
    localStorage.removeItem('platformAdminToken');
    setIsAuthenticated(false);
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ isAuthenticated, user, loading, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};
